/**
 * Minimal hard body separation — position clamp after movement.
 * Soft-sep (velocity impulse) remains the ally↔ally crowd layer; this kit
 * enforces a hard min distance for any two circles (boss / hostile / etc.).
 */

export type BodyCircle = { x: number; y: number; radius: number };

export type BodySepDir = { nx: number; ny: number };

function sepDir(
  ax: number,
  ay: number,
  bx: number,
  by: number,
  coincident?: BodySepDir,
): BodySepDir {
  const dx = ax - bx;
  const dy = ay - by;
  const len = Math.hypot(dx, dy);
  if (len >= 1e-3) return { nx: dx / len, ny: dy / len };
  const nx = coincident?.nx ?? 1;
  const ny = coincident?.ny ?? 0;
  const nlen = Math.hypot(nx, ny) || 1;
  return { nx: nx / nlen, ny: ny / nlen };
}

export type HostileHardMinSepOpts = {
  /**
   * Center-to-center weapon reach for the mover vs this other
   * (typically `other.radius + mover.attackRange`).
   */
  attackReach?: number;
  /** Margin inside attackReach; default 2. */
  reachEps?: number;
};

/**
 * Shared hard min-sep for any hostile pair (boss, add, …).
 * `min(rMover+rOther+pad, attackReach − eps)` when reach is provided so
 * melee/frontline can still stand on the ring and hit.
 */
export function hostileHardMinSep(
  moverRadius: number,
  otherRadius: number,
  pad: number,
  opts?: HostileHardMinSepOpts,
): number {
  const body = moverRadius + otherRadius + Math.max(0, pad);
  const reach = opts?.attackReach;
  if (reach == null || !Number.isFinite(reach)) return body;
  const eps = opts?.reachEps ?? 2;
  const floor = otherRadius + Math.max(2, moverRadius * 0.25);
  return Math.min(body, Math.max(floor, reach - eps));
}

/**
 * Project a cartesian formation offset onto the tangent of
 * `center → (px,py)`. Keeps ring depth stable so slot spread cannot shove
 * a unit radially past melee reach (boss or large add).
 */
export function projectTangentialSlotOnCircle(
  px: number,
  py: number,
  centerX: number,
  centerY: number,
  ox: number,
  oy: number,
  bearingFallback?: { x: number; y: number },
): { x: number; y: number } {
  let dx = px - centerX;
  let dy = py - centerY;
  let len = Math.hypot(dx, dy);
  if (len < 1) {
    if (bearingFallback) {
      dx = bearingFallback.x - centerX;
      dy = bearingFallback.y - centerY;
      len = Math.hypot(dx, dy);
    }
    if (len < 1) {
      dx = 0;
      dy = 1;
      len = 1;
    }
  }
  const rx = dx / len;
  const ry = dy / len;
  const txHat = -ry;
  const tyHat = rx;
  const along = ox * txHat + oy * tyHat;
  return { x: px + txHat * along, y: py + tyHat * along };
}

/**
 * Clamp a world point into an annulus `[minR, maxR]` around a circle center.
 * Used for attackable stand rings vs any radius hostile (boss / large add).
 */
export function clampPointToAnnulus(
  px: number,
  py: number,
  centerX: number,
  centerY: number,
  minR: number,
  maxR: number,
  bearingFallback?: { x: number; y: number },
): { x: number; y: number } {
  const lo = Math.min(minR, maxR);
  const hi = Math.max(minR, maxR);
  const d = Math.hypot(px - centerX, py - centerY);
  if (d <= hi && d >= lo) return { x: px, y: py };
  let dx = px - centerX;
  let dy = py - centerY;
  let len = Math.hypot(dx, dy);
  if (len < 1) {
    if (bearingFallback) {
      dx = bearingFallback.x - centerX;
      dy = bearingFallback.y - centerY;
      len = Math.hypot(dx, dy);
    }
    if (len < 1) {
      dx = 0;
      dy = 1;
      len = 1;
    }
  }
  const r = Math.min(hi, Math.max(lo, d));
  return {
    x: centerX + (dx / len) * r,
    y: centerY + (dy / len) * r,
  };
}

/**
 * True when body is outside weapon reach but the goal is still closer to the
 * hostile — do not treat as arrived (soft-sep can park in the ≤6 deadzone).
 */
export function shouldKeepClosingOnReach(
  bodyX: number,
  bodyY: number,
  goalX: number,
  goalY: number,
  hostileX: number,
  hostileY: number,
  attackReach: number,
  slack = 0.5,
): boolean {
  const bodyD = Math.hypot(bodyX - hostileX, bodyY - hostileY);
  if (bodyD <= attackReach) return false;
  const goalD = Math.hypot(goalX - hostileX, goalY - hostileY);
  return goalD < bodyD - slack;
}

/**
 * Push `movable` fully onto the ring around `fixed`
 * (`fixed.radius + movable.radius + pad`). Coincident: use `coincident` for
 * direction only — never early-return on a wrong length (see KNOWN_TRAPS).
 */
export function separateFromFixed(
  movable: BodyCircle,
  fixed: BodyCircle,
  pad: number,
  coincident?: BodySepDir,
  /** When set, replaces `fixed.r + movable.r + pad` (e.g. reach-capped sep). */
  minSepOverride?: number,
): { x: number; y: number; separated: boolean; minSep: number } {
  const minSep =
    typeof minSepOverride === "number" && Number.isFinite(minSepOverride)
      ? Math.max(1, minSepOverride)
      : fixed.radius + movable.radius + Math.max(0, pad);
  const dx = movable.x - fixed.x;
  const dy = movable.y - fixed.y;
  const len = Math.hypot(dx, dy);
  if (len >= minSep) {
    return { x: movable.x, y: movable.y, separated: false, minSep };
  }
  const { nx, ny } = sepDir(movable.x, movable.y, fixed.x, fixed.y, coincident);
  return {
    x: fixed.x + nx * minSep,
    y: fixed.y + ny * minSep,
    separated: true,
    minSep,
  };
}

/**
 * Split push so both circles sit on a shared minSep diameter.
 * Equal mass; callers clamp to arena afterward.
 */
export function separateCircles(
  a: BodyCircle,
  b: BodyCircle,
  pad: number,
  coincident?: BodySepDir,
  minSepOverride?: number,
): {
  ax: number;
  ay: number;
  bx: number;
  by: number;
  separated: boolean;
  minSep: number;
} {
  const minSep =
    typeof minSepOverride === "number" && Number.isFinite(minSepOverride)
      ? Math.max(1, minSepOverride)
      : a.radius + b.radius + Math.max(0, pad);
  const dx = a.x - b.x;
  const dy = a.y - b.y;
  const len = Math.hypot(dx, dy);
  if (len >= minSep) {
    return {
      ax: a.x,
      ay: a.y,
      bx: b.x,
      by: b.y,
      separated: false,
      minSep,
    };
  }
  const { nx, ny } = sepDir(a.x, a.y, b.x, b.y, coincident);
  if (len < 1e-3) {
    const half = minSep * 0.5;
    const mx = (a.x + b.x) * 0.5;
    const my = (a.y + b.y) * 0.5;
    return {
      ax: mx + nx * half,
      ay: my + ny * half,
      bx: mx - nx * half,
      by: my - ny * half,
      separated: true,
      minSep,
    };
  }
  const push = (minSep - len) * 0.5;
  return {
    ax: a.x + nx * push,
    ay: a.y + ny * push,
    bx: b.x - nx * push,
    by: b.y - ny * push,
    separated: true,
    minSep,
  };
}
